/**
 * Type-safe localStorage utility with SSR safety and error handling
 *
 * Usage:
 * - storage.get<T>(key) - Get item with type safety
 * - storage.set(key, value) - Set item (auto-stringified)
 * - storage.remove(key) - Remove item
 * - storage.clear() - Clear all items
 *
 * Benefits:
 * - SSR-safe (checks for window)
 * - Type-safe with generics
 * - Automatic JSON parse/stringify
 * - Error handling with logging
 * - Quota exceeded handling
 */

import { logger } from '@/lib/platform/utils/logger';

export const storage = {
  /**
   * Get item from localStorage with type safety
   * @param key Storage key
   * @returns Parsed value or null if not found/error
   */
  get: <T>(key: string): T | null => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return null;
      }

      // Try to parse JSON, if it fails return raw string
      try {
        return JSON.parse(item) as T;
      } catch {
        // If JSON parse fails, return raw value (for simple strings)
        return item as T;
      }
    } catch (error) {
      logger.error(`Storage get error for key "${key}":`, error);
      return null;
    }
  },

  /**
   * Set item in localStorage
   * @param key Storage key
   * @param value Value to store (will be JSON stringified)
   */
  set: (key: string, value: unknown): void => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
    } catch (error) {
      // Handle quota exceeded error
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        logger.error(`Storage quota exceeded when setting key "${key}"`);
        // Could implement cleanup strategy here
      } else {
        logger.error(`Storage set error for key "${key}":`, error);
      }
    }
  },

  /**
   * Remove item from localStorage
   * @param key Storage key
   */
  remove: (key: string): void => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error(`Storage remove error for key "${key}":`, error);
    }
  },

  /**
   * Clear all items from localStorage
   * WARNING: This will remove ALL localStorage data
   */
  clear: (): void => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.clear();
    } catch (error) {
      logger.error('Storage clear error:', error);
    }
  },

  /**
   * Check if a key exists in localStorage
   * @param key Storage key
   * @returns true if key exists, false otherwise
   */
  has: (key: string): boolean => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      logger.error(`Storage has error for key "${key}":`, error);
      return false;
    }
  },

  /**
   * Get all keys in localStorage
   * @returns Array of all keys
   */
  keys: (): string[] => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      return Object.keys(localStorage);
    } catch (error) {
      logger.error('Storage keys error:', error);
      return [];
    }
  },
};

/**
 * Storage item with expiration support
 */
interface StorageItemWithExpiry<T> {
  value: T;
  expiry: number; // Timestamp in ms
}

/**
 * Extended storage utility with expiration support
 */
export const storageWithExpiry = {
  /**
   * Set item with expiration
   * @param key Storage key
   * @param value Value to store
   * @param ttlMs Time to live in milliseconds
   */
  set: <T>(key: string, value: T, ttlMs: number): void => {
    const item: StorageItemWithExpiry<T> = {
      value,
      expiry: Date.now() + ttlMs,
    };
    storage.set(key, item);
  },

  /**
   * Get item with expiration check
   * @param key Storage key
   * @returns Value if not expired, null otherwise
   */
  get: <T>(key: string): T | null => {
    const item = storage.get<StorageItemWithExpiry<T>>(key);

    if (!item) {
      return null;
    }

    // Check if expired
    if (Date.now() > item.expiry) {
      storage.remove(key);
      return null;
    }

    return item.value;
  },
};
